import { useNavigate } from 'react-router-dom';

import { useQuestsStore } from '../store/questsStore';
import { Analytics } from 'lib/AnalyticsLoader/analytics';

import { ROUTES } from 'shared/constants';
import { NULL_ADDRESS } from 'shared/constants/constants';
import { useAccount, usePublicClient, useWalletClient } from 'shared/hooks/use-web3';
import { useCurrenciesStore } from 'shared/store/currenciesStore';
import { useGlobalStore } from 'shared/store/globalStore';
import { useStartGameStore } from 'shared/store/start-game-store';

export const useStartJoinAllowance = () => {
  const navigate = useNavigate();
  const { address, chainId } = useAccount();
  const { approveAllowance, startGame, joinRoom } = useStartGameStore();
  const { fetchUserBalances, setSnapshotPoints } = useQuestsStore();
  const { selectedCurrency } = useCurrenciesStore();
  const invitedBy = useGlobalStore((state) => state.invitedBy);

  const publicClient = usePublicClient();
  const walletClient = useWalletClient();

  const approveAllowanceHandler = async (fullAllowance: boolean, bet: number) => {
    if (!walletClient || !publicClient || !selectedCurrency) return;

    await approveAllowance({
      amount: +bet,
      currency: selectedCurrency,
      userAddress: address as `0x${string}`,
      walletClient,
      publicClient,
      fullAllowance,
      joinGame: false,
    });
  };

  const startGameHandler = async (bet: number, playerToInvite?: `0x${string}`) => {
    if (!walletClient || !publicClient || !selectedCurrency || !bet) return;

    const output = await startGame({
      wins: 3,
      currency: selectedCurrency.address,
      betAmount: BigInt(Math.floor(+bet * 10 ** selectedCurrency!.decimals)),
      playerB: (playerToInvite || NULL_ADDRESS) as `0x${string}`,
      referrer: invitedBy,
      walletClient,
      publicClient,
      userAddress: address as `0x${string}`,
    });

    await fetchUserBalances(publicClient, address, true);

    if (output) {
      // Analytics tracking
      Analytics.createRoomEvent(address, invitedBy, bet, selectedCurrency.symbol);
      // Navigate to room after delay
      setTimeout(() => {
        navigate(`${ROUTES.ROOM}/${chainId}/${output}`);
      }, 1000);
    }
  };

  const joinRoomHandler = async (roomId: number, bet?: number) => {
    if (!walletClient || !publicClient || !selectedCurrency) return;

    try {
      await joinRoom({
        roomId: BigInt(roomId),
        referrer: invitedBy,
        walletClient,
        publicClient,
        userAddress: address as `0x${string}`,
      });

      await fetchUserBalances(publicClient, address, true);

      // Analytics tracking
      Analytics.joinRoomEvent(address, invitedBy, bet, selectedCurrency.symbol);
      // Navigate to room after delay
      setTimeout(() => {
        navigate(`${ROUTES.ROOM}/${chainId}/${roomId}`);
      }, 1000);
    } catch (e) {
      console.error(e);
    }
  };

  return {
    approveAllowanceHandler,
    startGameHandler,
    joinRoomHandler,
  };
};
